/**
 * useTrainingProfile Hook
 * Loads training profiles for simulation mode and the currently selected
 * profile detail record.
 *
 * @module useTrainingProfile
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createCancellation } from '../services/apiClient';
import {
  getAvailableTrainingProfiles,
  getPreviewTrainingProfile,
  getPublicTrainingProfile,
} from '../services/trainingProfilesService';

const parseProfileId = (value) => {
  const numericValue = value ? Number(value) : null;
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

/**
 * Manage available training profiles and selected profile detail state.
 * @param {Object} options - Hook options
 * @param {number|string|null} [options.initialProfileId] - Initial selected profile ID
 * @param {number|null} [options.countryId] - Optional country filter
 * @param {boolean} [options.enabled=true] - Whether to load profiles
 * @param {'public'|'preview'} [options.accessMode='public'] - Selected profile fetch mode
 * @returns {Object} Training profile selection and loading state
 */
export const useTrainingProfile = ({ initialProfileId = null, countryId = null, enabled = true, accessMode = 'public' } = {}) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(parseProfileId(initialProfileId));
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    const nextId = parseProfileId(initialProfileId);
    if (nextId !== selectedProfileId) {
      setSelectedProfileId(nextId);
    }
  }, [enabled, initialProfileId, selectedProfileId]);

  const loadProfiles = useCallback(async () => {
    if (!enabled) return [];

    if (accessMode === 'preview') {
      setProfiles([]);
      return [];
    }

    const { signal } = createCancellation();
    setLoadingProfiles(true);
    setError(null);

    try {
      const data = await getAvailableTrainingProfiles(
        countryId ? { countryId } : {},
        { signal, suppressNotification: true }
      );
      setProfiles(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setError(err);
      }
      return [];
    } finally {
      setLoadingProfiles(false);
    }
  }, [accessMode, countryId, enabled]);

  const loadProfile = useCallback(async (profileId) => {
    if (!enabled) return null;

    if (!profileId) {
      setSelectedProfile(null);
      return null;
    }

    const { signal } = createCancellation();
    setLoadingProfile(true);
    setError(null);

    try {
      const loadProfileByMode = accessMode === 'preview'
        ? getPreviewTrainingProfile
        : getPublicTrainingProfile;
      const data = await loadProfileByMode(profileId, { signal, suppressNotification: true });
      setSelectedProfile(data);
      return data;
    } catch (err) {
      setSelectedProfile(null);
      if (err?.name !== 'AbortError') {
        setError(err);
      }
      return null;
    } finally {
      setLoadingProfile(false);
    }
  }, [accessMode, enabled]);

  useEffect(() => {
    if (enabled) {
      loadProfiles();
    }
  }, [enabled, loadProfiles]);

  useEffect(() => {
    if (enabled) {
      loadProfile(selectedProfileId);
    }
  }, [enabled, loadProfile, selectedProfileId]);

  const selectedProfileSummary = useMemo(
    () => profiles.find(profile => profile.id === selectedProfileId) || null,
    [profiles, selectedProfileId]
  );

  return {
    profiles,
    selectedProfileId,
    selectedProfile,
    selectedProfileSummary,
    loadingProfiles,
    loadingProfile,
    loading: loadingProfiles || loadingProfile,
    error,
    setSelectedProfileId,
    reloadProfiles: loadProfiles,
    reloadSelectedProfile: () => loadProfile(selectedProfileId),
  };
};

export default useTrainingProfile;
